import type { CollectionRole, Root, SavedView, SidebarGroup } from "../../domain/app-types.ts";
import type { Category } from "../../domain/entities.ts";
import { destinationSectionsView } from "./destination-view.ts";
import type { DestinationRef } from "./destinations.ts";
import type { NavigationState } from "./state.ts";
import { sidebarSpacesView } from "./sidebar-view.ts";

export interface SidebarRuntimeCapabilities {
  navigation: NavigationState;
  roots(): Root[];
  activeRootId(): string | null;
  activeCollectionId(): string | null;
  collectionRoles(): ReadonlyMap<string, CollectionRole>;
  savedViews(): SavedView[];
  persistRecent(destinations: DestinationRef[]): Promise<void>;
  currentGroup(): SidebarGroup | null;
  updateGroupDisclosures(): void;
  updateSpaceDisclosures(): void;
  closeMenu(restoreFocus?: boolean): void;
  icon(name: string): string;
}

export function createSidebarRuntime(capabilities: SidebarRuntimeCapabilities) {
  return {
    currentGroup: capabilities.currentGroup,
    updateGroupDisclosures: capabilities.updateGroupDisclosures,
    updateSpaceDisclosures: capabilities.updateSpaceDisclosures,
    closeMenu: capabilities.closeMenu,

    async rememberVisited(destination: DestinationRef): Promise<void> {
      capabilities.navigation.rememberDestination(destination);
      await capabilities.persistRecent(capabilities.navigation.recentDestinations);
    },

    destinationSections(categories: Category[]): string {
      const result = destinationSectionsView({
        roots: capabilities.roots(),
        categories,
        savedViews: capabilities.savedViews(),
        favoriteDestinations: capabilities.navigation.favoriteDestinations,
        recentDestinations: capabilities.navigation.recentDestinations,
        favoriteTaskIds: capabilities.navigation.favoriteTaskIds,
        recentTasks: capabilities.navigation.recentTasks,
        icon: capabilities.icon,
      });
      capabilities.navigation.favoriteDestinations = result.favoriteDestinations;
      capabilities.navigation.recentDestinations = result.recentDestinations;
      return result.html;
    },

    spaces(categories: Category[]): string {
      return sidebarSpacesView({
        roots: capabilities.roots(),
        categories,
        expandedSpaceId: capabilities.navigation.expandedSpaceId,
        openMenu: capabilities.navigation.openMenu,
        activeRootId: capabilities.activeRootId(),
        activeCategoryId: capabilities.activeCollectionId(),
        collectionRoles: capabilities.collectionRoles(),
        favorites: capabilities.navigation.favoriteDestinations,
        icon: capabilities.icon,
      });
    },
  };
}
